import { Disclosure } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/outline';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';

import clsxm from '@/lib/clsxm';
import { getMediaTemplateSeoContent } from '@/lib/mediaTemplateSeoContent';

import { HappyUsers } from '@/components/landing/HappyUsers';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import Seo from '@/components/Seo';

import {
  getMediaTemplatesByKind,
  ImageTool,
  MediaTemplateKind,
} from '@/constants/mediaTemplates';

const Footer = dynamic(() => import('@/components/layout/Footer'));
const CallToAction = dynamic(() => import('@/components/CallToAction'));

interface MediaTemplateLandingPageProps {
  template: ImageTool;
  kind: MediaTemplateKind;
  parentPath: string;
  parentLabel: string;
}

// Detail previews keep the full composition, including very tall portraits.
const getPreviewAspectRatio = ({ aspect }: ImageTool): number => {
  const [width, height] = (aspect || '16:9').split(':').map(Number);
  const ratio = width / height;
  return width > 0 && height > 0 && Number.isFinite(ratio) ? ratio : 16 / 9;
};

const MediaTemplateLandingPage = ({
  template,
  kind,
  parentPath,
  parentLabel,
}: MediaTemplateLandingPageProps) => {
  const { t } = useLocale('media-templates');
  const content = getMediaTemplateSeoContent(template, kind);
  const previewAspectRatio = getPreviewAspectRatio(template);
  const previewMaxWidth = Math.round(600 * previewAspectRatio);
  const relatedTemplates = getMediaTemplatesByKind(kind)
    .filter((item) => item.slug !== template.slug)
    .slice(0, 6);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: t(parentLabel),
        item: `https://easy-peasy.ai${parentPath}`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: `${t(template.title)} Template`,
      },
    ],
  };

  const howToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: `How to use the ${t(template.title)} template`,
    description: t(content.intro),
    step: content.steps.map((step, idx) => ({
      '@type': 'HowToStep',
      position: idx + 1,
      text: t(step),
    })),
  };

  const faqSchema =
    content.faqs.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: content.faqs.map((faq) => ({
            '@type': 'Question',
            name: t(faq.question),
            acceptedAnswer: {
              '@type': 'Answer',
              text: t(faq.answer),
            },
          })),
        }
      : null;

  return (
    <main className='bg-white dark:bg-slate-900'>
      <Seo
        templateTitle={
          template.seo_title
            ? t(template.seo_title)
            : `${t(template.title)} ${t('Template')}`
        }
        description={
          template.seo_description
            ? t(template.seo_description)
            : `${t(template.description)} ${t(`Explore this AI ${kind} template and create your own result quickly.`)}`
        }
        image={template.image}
      />
      <Head>
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(breadcrumbSchema),
          }}
        />
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(howToSchema),
          }}
        />
        {faqSchema && (
          <script
            type='application/ld+json'
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(faqSchema),
            }}
          />
        )}
      </Head>

      {/* ═══════════════════════════════════════════
          HERO — dark immersive section
      ═══════════════════════════════════════════ */}
      <div className='relative overflow-hidden bg-gray-950'>
        {/* Ambient gradient blobs */}
        <div
          className='pointer-events-none absolute -right-40 -top-40 h-[600px] w-[600px] rounded-full opacity-[.15] blur-[100px]'
          aria-hidden='true'
          style={{
            background:
              'radial-gradient(circle, rgba(139,92,246,.6) 0%, transparent 70%)',
          }}
        />
        <div
          className='pointer-events-none absolute -bottom-24 -left-24 h-[400px] w-[400px] rounded-full opacity-[.12] blur-[80px]'
          aria-hidden='true'
          style={{
            background:
              'radial-gradient(circle, rgba(236,72,153,.5) 0%, transparent 70%)',
          }}
        />

        {/* Navigation */}
        <div className='relative z-20'>
          <LandingPageNavigation variant='light' />
        </div>

        <div className='relative mx-auto max-w-7xl px-6 pb-24 pt-8 lg:px-8'>
          {/* Breadcrumb */}
          <nav className='mb-10 flex items-center gap-2 text-sm text-gray-500'>
            <Link
              href={parentPath}
              className='transition-colors hover:text-gray-300'
            >
              {t(parentLabel)}
            </Link>
            <svg
              className='h-3.5 w-3.5 text-gray-700'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M9 5l7 7-7 7'
              />
            </svg>
            <span className='text-gray-400'>{t(template.title)}</span>
          </nav>

          <div className='grid items-center gap-12 lg:grid-cols-2 lg:gap-16'>
            {/* ── Left column: copy ── */}
            <div>
              <span className='mb-5 inline-flex items-center gap-1.5 rounded-full bg-violet-500/10 px-4 py-1.5 text-sm font-medium text-violet-300 ring-1 ring-inset ring-violet-500/20'>
                <svg
                  className='h-3.5 w-3.5'
                  viewBox='0 0 24 24'
                  fill='currentColor'
                >
                  <path d='M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z' />
                </svg>
                {t(
                  kind === 'video' ? 'AI Video Template' : 'AI Image Template',
                )}
              </span>

              <h1 className='text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-[3.5rem] lg:leading-[1.1]'>
                {t(template.title)}
              </h1>

              <p className='mt-6 max-w-xl text-lg leading-relaxed text-gray-400'>
                {t(content.intro)}
              </p>

              <div className='mt-10 flex flex-wrap items-center gap-4'>
                <Link
                  href={template.uri}
                  className='group inline-flex items-center rounded-xl bg-white px-7 py-3.5 text-base font-semibold text-gray-900 shadow-lg shadow-white/5 transition-all duration-200 hover:scale-[1.03] hover:shadow-xl hover:shadow-white/10'
                >
                  {t('Use this template')}
                  <svg
                    className='ml-2 h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M13 7l5 5m0 0l-5 5m5-5H6'
                    />
                  </svg>
                </Link>
                <a
                  href='#how-it-works'
                  className='inline-flex items-center gap-1 text-sm font-medium text-gray-400 transition-colors hover:text-white'
                >
                  {t('See how it works')}
                  <svg
                    className='h-4 w-4'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M19 9l-7 7-7-7'
                    />
                  </svg>
                </a>
              </div>

              <div className='mt-10'>
                <HappyUsers fromLandingPage={true} />
              </div>
            </div>

            {/* ── Right column: media showcase ── */}
            <div
              className='relative mx-auto w-full'
              style={{ maxWidth: previewMaxWidth }}
            >
              <div
                className='relative overflow-hidden rounded-2xl bg-gray-900 shadow-2xl ring-1 ring-white/[.08]'
                style={{ aspectRatio: previewAspectRatio }}
              >
                {template.isVideoPreview ? (
                  <video
                    src={template.image}
                    className='absolute inset-0 h-full w-full object-contain'
                    autoPlay
                    muted
                    loop
                    playsInline
                    controls
                  />
                ) : (
                  <Image
                    src={template.image}
                    alt={t(template.title)}
                    fill
                    className='object-contain'
                    sizes={`(max-width: 1023px) min(calc(100vw - 48px), ${previewMaxWidth}px), min(calc(50vw - 64px), ${Math.min(576, previewMaxWidth)}px)`}
                    preload
                  />
                )}
              </div>
              {/* Glow beneath the media frame */}
              <div
                className='pointer-events-none absolute -bottom-6 left-1/2 -z-10 h-40 w-4/5 -translate-x-1/2 rounded-full blur-3xl'
                aria-hidden='true'
                style={{
                  background:
                    'radial-gradient(ellipse, rgba(139,92,246,.18) 0%, transparent 70%)',
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════
          BENEFITS
      ═══════════════════════════════════════════ */}
      <section className='relative border-b border-gray-100 bg-gray-50 py-24 dark:border-slate-800 dark:bg-slate-800/40'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto max-w-2xl text-center'>
            <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl'>
              {t('Why use this template')}
            </h2>
            <p className='mt-4 text-lg text-gray-600 dark:text-slate-400'>
              {t(
                'Skip the blank page \u2014 start from a structure that works.',
              )}
            </p>
          </div>

          <div className='mx-auto mt-14 grid max-w-5xl gap-6 lg:grid-cols-3'>
            {content.benefits.map((benefit, i) => {
              const icons = [
                // Lightbulb — proven prompt
                <svg
                  key='icon-0'
                  className='h-6 w-6'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18'
                  />
                </svg>,
                // Lightning — faster generation
                <svg
                  key='icon-1'
                  className='h-6 w-6'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z'
                  />
                </svg>,
                // Palette — customise
                <svg
                  key='icon-2'
                  className='h-6 w-6'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a6.776 6.776 0 00-3.42-3.42'
                  />
                </svg>,
              ];

              return (
                <div
                  key={benefit}
                  className='group rounded-2xl bg-white p-8 shadow-sm ring-1 ring-gray-200/60 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md dark:bg-slate-800 dark:ring-slate-700/60'
                >
                  <div className='mb-5 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-violet-100 text-violet-600 dark:bg-violet-500/10 dark:text-violet-400'>
                    {icons[i]}
                  </div>
                  <p className='text-[15px] leading-relaxed text-gray-700 dark:text-slate-300'>
                    {t(benefit)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          HOW IT WORKS — numbered timeline
      ═══════════════════════════════════════════ */}
      <section id='how-it-works' className='bg-white py-24 dark:bg-slate-900'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto max-w-2xl text-center'>
            <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl'>
              {t('How to get the best output')}
            </h2>
            <p className='mt-4 text-lg text-gray-600 dark:text-slate-400'>
              {t('Follow these steps for production-ready results every time.')}
            </p>
          </div>

          <div className='mx-auto mt-16 max-w-3xl'>
            {content.steps.map((step, idx) => (
              <div key={step} className='relative flex gap-6 pb-12 last:pb-0'>
                {/* Connecting line */}
                {idx < content.steps.length - 1 && (
                  <div className='absolute left-[23px] top-14 h-[calc(100%-3.5rem)] w-px bg-gradient-to-b from-violet-300 to-violet-100 dark:from-violet-600 dark:to-violet-900' />
                )}
                {/* Step circle */}
                <div className='relative z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-violet-600 text-base font-bold text-white shadow-lg shadow-violet-600/25'>
                  {idx + 1}
                </div>
                <div className='pt-2.5'>
                  <p className='text-[17px] leading-relaxed text-gray-700 dark:text-slate-300'>
                    {t(step)}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Mid-page CTA */}
          <div className='mx-auto mt-16 flex justify-center'>
            <Link
              href={template.uri}
              className='group inline-flex items-center rounded-xl bg-violet-600 px-8 py-3.5 text-base font-semibold text-white shadow-lg shadow-violet-600/25 transition-all duration-200 hover:bg-violet-500 hover:shadow-xl hover:shadow-violet-600/30'
            >
              {t("Try it now \u2014 it's free")}
              <svg
                className='ml-2 h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M13 7l5 5m0 0l-5 5m5-5H6'
                />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          USE CASES  +  TIPS — two-column
      ═══════════════════════════════════════════ */}
      <section className='border-t border-gray-100 bg-gray-50 py-24 dark:border-slate-800 dark:bg-slate-800/40'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='grid gap-8 lg:grid-cols-2'>
            {/* Use cases */}
            <div className='rounded-2xl bg-white p-8 shadow-sm ring-1 ring-gray-200/60 dark:bg-slate-800 dark:ring-slate-700/60'>
              <h3 className='text-xl font-bold text-gray-900 dark:text-white'>
                {t('Best for')}
              </h3>
              <ul className='mt-6 space-y-4'>
                {content.useCases.map((item) => (
                  <li key={item} className='flex items-start gap-3'>
                    <svg
                      className='mt-0.5 h-5 w-5 shrink-0 text-emerald-500'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        d='M5 13l4 4L19 7'
                      />
                    </svg>
                    <span className='text-[15px] text-gray-700 dark:text-slate-300'>
                      {t(item)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Tips */}
            <div className='rounded-2xl bg-white p-8 shadow-sm ring-1 ring-gray-200/60 dark:bg-slate-800 dark:ring-slate-700/60'>
              <h3 className='text-xl font-bold text-gray-900 dark:text-white'>
                {t('Prompt tips')}
              </h3>
              <ul className='mt-6 space-y-4'>
                {content.tips.map((tip) => (
                  <li key={tip} className='flex items-start gap-3'>
                    <svg
                      className='mt-0.5 h-5 w-5 shrink-0 text-amber-500'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                      strokeWidth={1.5}
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        d='M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18'
                      />
                    </svg>
                    <span className='text-[15px] text-gray-700 dark:text-slate-300'>
                      {t(tip)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          FAQs — accordion with Schema.org microdata
      ═══════════════════════════════════════════ */}
      {content.faqs.length > 0 && (
        <section className='bg-white py-24 dark:bg-slate-900'>
          <div className='mx-auto max-w-7xl px-6 lg:px-8'>
            <div
              className='mx-auto max-w-3xl divide-y-2 divide-gray-200 dark:divide-slate-700'
              itemScope
              itemType='https://schema.org/FAQPage'
            >
              <h2 className='text-center text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl'>
                {t('Frequently asked questions')}
              </h2>
              <dl className='mt-8 space-y-6 divide-y divide-gray-200 dark:divide-slate-700'>
                {content.faqs.map((faq) => (
                  <Disclosure
                    as='div'
                    key={faq.question}
                    className='pt-6'
                    itemScope
                    itemProp='mainEntity'
                    itemType='https://schema.org/Question'
                  >
                    {({ open }) => (
                      <>
                        <dt className='text-lg'>
                          <Disclosure.Button className='flex w-full items-start justify-between text-left text-gray-400 dark:text-slate-400'>
                            <span
                              className='font-medium text-gray-900 dark:text-slate-200'
                              itemProp='name'
                            >
                              {t(faq.question)}
                            </span>
                            <span className='ml-6 flex h-7 items-center'>
                              <ChevronDownIcon
                                className={clsxm(
                                  open ? '-rotate-180' : 'rotate-0',
                                  'h-6 w-6 transform transition-transform duration-200',
                                )}
                                aria-hidden='true'
                              />
                            </span>
                          </Disclosure.Button>
                        </dt>
                        <Disclosure.Panel
                          as='dd'
                          className='mt-2 pr-12'
                          itemScope
                          itemProp='acceptedAnswer'
                          unmount={false}
                          itemType='https://schema.org/Answer'
                        >
                          <p
                            className='text-base leading-relaxed text-gray-600 dark:text-slate-400'
                            itemProp='text'
                          >
                            {t(faq.answer)}
                          </p>
                        </Disclosure.Panel>
                      </>
                    )}
                  </Disclosure>
                ))}
              </dl>
            </div>
          </div>
        </section>
      )}

      {/* ═══════════════════════════════════════════
          RELATED TEMPLATES — with full previews
      ═══════════════════════════════════════════ */}
      {relatedTemplates.length > 0 && (
        <section className='bg-white py-24 dark:bg-slate-900'>
          <div className='mx-auto max-w-7xl px-6 lg:px-8'>
            <div className='mb-14 text-center'>
              <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl'>
                {t(
                  kind === 'video'
                    ? 'Explore more video templates'
                    : 'Explore more image templates',
                )}
              </h2>
              <p className='mt-4 text-lg text-gray-600 dark:text-slate-400'>
                {t(
                  'Browse other templates to find the perfect starting point.',
                )}
              </p>
            </div>

            <div className='columns-1 gap-6 sm:columns-2 lg:columns-3'>
              {relatedTemplates.map((item) => (
                <Link
                  key={item.slug}
                  href={`${parentPath}/templates/${item.slug}`}
                  className='group mb-6 block break-inside-avoid overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-lg dark:border-slate-700 dark:bg-slate-800'
                >
                  <div
                    className='relative overflow-hidden bg-gray-100 dark:bg-slate-900'
                    style={{ aspectRatio: getPreviewAspectRatio(item) }}
                  >
                    {item.isVideoPreview ? (
                      <video
                        src={item.image}
                        className='absolute inset-0 h-full w-full object-contain'
                        autoPlay
                        muted
                        loop
                        playsInline
                      />
                    ) : (
                      <Image
                        src={item.image}
                        alt={t(item.title)}
                        fill
                        className='object-contain'
                        sizes='(max-width: 639px) calc(100vw - 48px), (max-width: 1023px) calc(50vw - 36px), (max-width: 1279px) calc((100vw - 112px) / 3), 389px'
                      />
                    )}
                  </div>
                  <div className='p-5'>
                    <h3 className='text-lg font-semibold text-gray-900 dark:text-slate-100'>
                      {t(item.title)}
                    </h3>
                    <p className='mt-2 line-clamp-2 text-sm text-gray-600 dark:text-slate-300'>
                      {t(item.description)}
                    </p>
                    <span className='mt-4 inline-flex items-center text-sm font-medium text-violet-600 transition-colors group-hover:text-violet-500 dark:text-violet-400'>
                      {t('View template')}
                      <svg
                        className='ml-1 h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5'
                        fill='none'
                        viewBox='0 0 24 24'
                        stroke='currentColor'
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          d='M13 7l5 5m0 0l-5 5m5-5H6'
                        />
                      </svg>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══════════════════════════════════════════
          CTA + FOOTER
      ═══════════════════════════════════════════ */}
      <CallToAction preset={kind === 'video' ? 'ai-videos' : 'ai-images'} />
      <Footer />
    </main>
  );
};

export default MediaTemplateLandingPage;
